import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Separator } from "@/shared/components/ui/separator";
import { Badge } from "@/shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  DetailHeaderActions,
  DetailHeaderInfo,
} from "@/shared/components/detail-header";
import { DisplayInputResultLabels } from "../display-input-result-labels";
import type {
  InputResultLabel,
  TemplateOrigin,
} from "../../../../queries/reports";
import { TemplateOriginLink } from "../template-origin-link";
import { TruncatedHoverText } from "../truncated-hover-text";

const EMPTY_DISPLAY_INPUT_RESULTS: InputResultLabel[] = [];

interface ReportDetailsHeaderProps {
  reportName?: string;
  reportStatus?: string;
  reportType?: string;
  displayDescription?: string;
  userContext?: string;
  displayInputResults?: InputResultLabel[];
  templateOrigin?: TemplateOrigin;
  createdAt?: string;
  updatedAt?: string;
  isLoading: boolean;
  isRestarting: boolean;
  isExporting: boolean;
  isDeleting?: boolean;
  isReadOnly?: boolean;
  onRestart: () => void;
  onExport: () => void;
  onDeleteReport?: () => void;
  onUpdateMetadata?: (values: {
    displayName: string;
    context: string;
  }) => Promise<void>;
}

export function ReportDetailsHeader({
  reportName,
  reportStatus,
  reportType,
  displayDescription,
  userContext,
  displayInputResults,
  templateOrigin,
  createdAt,
  updatedAt,
  isLoading,
  isRestarting,
  isExporting,
  isDeleting = false,
  isReadOnly = false,
  onRestart,
  onExport,
  onDeleteReport,
  onUpdateMetadata,
}: ReportDetailsHeaderProps) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRerunDialog, setShowRerunDialog] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [contextDraft, setContextDraft] = useState("");
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const resolvedDisplayInputResults =
    displayInputResults ?? EMPTY_DISPLAY_INPUT_RESULTS;

  const handleRerunReport = () => {
    setShowRerunDialog(false);
    onRestart();
  };

  const handleSkipRerun = () => {
    setShowRerunDialog(false);
  };

  const handleEditReport = () => {
    setNameDraft(reportName ?? "");
    setContextDraft(displayDescription ?? "");
    setMetadataError(null);
    setShowEditDialog(true);
  };

  const handleSaveMetadata = async () => {
    if (!onUpdateMetadata || isReadOnly) return;
    const displayName = nameDraft.trim();
    const context = contextDraft.trim();
    const contextChanged = context !== (displayDescription ?? "").trim();
    setIsSavingMetadata(true);
    setMetadataError(null);
    try {
      await onUpdateMetadata({ displayName, context });
      setShowEditDialog(false);
      if (contextChanged) setShowRerunDialog(true);
    } catch {
      setMetadataError("Failed to save report details");
    } finally {
      setIsSavingMetadata(false);
    }
  };

  return (
    <>
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <DetailHeaderInfo
          title={reportName}
          titleFallback="Report"
          status={reportStatus}
          type={reportType}
          description={displayDescription}
          createdAt={createdAt}
          updatedAt={updatedAt}
          emptyDescriptionLabel="No context provided"
          isDescriptionEditable={false}
          titleAddon={
            templateOrigin || isReadOnly || onUpdateMetadata ? (
              <span className="flex flex-wrap items-center gap-2">
                {templateOrigin && (
                  <TemplateOriginLink origin={templateOrigin} />
                )}
                {isReadOnly && (
                  <Badge variant="outline" className="text-xs">
                    Read-only
                  </Badge>
                )}
                {!isReadOnly && onUpdateMetadata && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleEditReport}
                  >
                    Edit report
                  </Button>
                )}
              </span>
            ) : undefined
          }
        />
        <DetailHeaderActions
          isLoading={isLoading}
          isPrimaryPending={isRestarting}
          isExporting={isExporting}
          isDeleting={isDeleting}
          onPrimary={onRestart}
          primaryLabel="Rerun report"
          isPrimaryDisabled={
            isLoading ||
            isRestarting ||
            reportStatus?.toLowerCase() !== "completed"
          }
          onExport={onExport}
          onDelete={onDeleteReport}
          showPrimary={!isReadOnly}
          showDelete={!isReadOnly}
          deleteTitle="Delete Report"
          deleteDescription="Are you sure you want to delete this report? This action cannot be undone. All report steps and data will be permanently removed."
          deleteConfirmLabel="Delete Report"
        />
      </div>
      <div className="mt-0.5 min-w-0 space-y-1">
        <div className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
          <span className="shrink-0 font-medium text-foreground">
            Additional Context:
          </span>
          <TruncatedHoverText
            text={
              userContext?.trim() ? userContext : "No user context provided"
            }
            className="max-w-[min(60rem,calc(100vw-12rem))]"
          />
        </div>
        <DisplayInputResultLabels labels={resolvedDisplayInputResults} />
      </div>
      <Separator />

      <Dialog
        open={showEditDialog}
        onOpenChange={(open) => {
          if (isSavingMetadata) return;
          setShowEditDialog(open);
          if (!open) setMetadataError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit report</DialogTitle>
            <DialogDescription>
              Update the report name and context together.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="report-display-name">Report name</Label>
              <Input
                id="report-display-name"
                value={nameDraft}
                onChange={(event) => setNameDraft(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="report-context">Context</Label>
              <Textarea
                id="report-context"
                value={contextDraft}
                onChange={(event) => setContextDraft(event.target.value)}
                placeholder="Enter context for this report..."
                rows={4}
              />
            </div>
            {metadataError && (
              <p role="alert" className="text-sm text-destructive">
                {metadataError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={isSavingMetadata}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveMetadata()}
              disabled={isSavingMetadata || !nameDraft.trim()}
            >
              {isSavingMetadata ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRerunDialog} onOpenChange={setShowRerunDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rerun Report</DialogTitle>
            <DialogDescription>
              The context has been updated. Would you like to rerun the entire
              report now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleSkipRerun}>
              Skip for now
            </Button>
            <Button
              onClick={handleRerunReport}
              disabled={
                isLoading ||
                isRestarting ||
                reportStatus?.toLowerCase() !== "completed"
              }
            >
              {isRestarting ? "Restarting..." : "Rerun Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
